import React, { FC, useCallback, useState } from 'react';
import { SetURLSearchParams } from 'react-router-dom';

import { BackToAllButton } from 'buyside/components/presentation/IndexPageListArea/indexPageListArea';
import Button, { BUTTON_WIDTHS } from 'buyside/components/presentation/Button/button';
import FilterChecklist from 'buyside/components/presentation/Filters/FilterChecklist/filterChecklist';
import FilterSelect from 'buyside/components/presentation/Filters/FilterSelect/filterSelect';
import FilterTextInput from 'buyside/components/presentation/Filters/FilterTextInput/filterTextInput';
import Modal from 'buyside/components/presentation/Modal/modal';
import SelectableDropdown from 'buyside/components/presentation/SelectableDropdown/selectableDropdown';

import { labelizedFilterListOptions } from 'buyside/components/presentation/Filters/filterUtil';

const {
  aumRanges,
  allocatorFirmTypes,
  cities,
  countries,
  sectors,
  geographies,
  assetClasses,
  fundTypes,
  fundStrategies
} = labelizedFilterListOptions;

import { AllocatorProfileCurrentUserQuery, Maybe } from 'interfaces/graphql-typings';

import styles from './allocatorProfileIndexPageStyles.scss';

const AllocatorProfileFilters: FC<{
  adminFundProfiles: NonNullable<
    AllocatorProfileCurrentUserQuery['currentUser']
  >['adminFundProfiles'];
} & { selectedFundId?: Maybe<string>; setSearchParams: SetURLSearchParams }> = ({
  adminFundProfiles,
  selectedFundId,
  setSearchParams
}) => {
  const [isInvestmentPreferencesModalOpen, setIsInvestmentPreferencesModalOpen] = useState(false);

  const renderOpenInvestmentPreferencesModalButton = useCallback(
    () => (
      <div>
        <Button
          className={styles.filterGroupModalButton}
          onClick={() => setIsInvestmentPreferencesModalOpen(true)}
          text="Investment Preferences"
          width={BUTTON_WIDTHS.FULL}
        />
      </div>
    ),
    []
  );

  return (
    <>
      {adminFundProfiles.length > 1 && (
        <SelectableDropdown title="Recommendations">
          {adminFundProfiles.map(fund => (
            <label key={fund.id} className={styles.fundLabel}>
              <input
                checked={selectedFundId === fund.id}
                className={styles.fundInput}
                type="radio"
                onChange={(): void =>
                  setSearchParams(prev => {
                    prev.set('recommended', 'true');
                    prev.set('fpid', `${fund.id}`);
                    return prev;
                  })
                }
              />
              For {fund.name}
            </label>
          ))}
          {selectedFundId && <BackToAllButton />}
        </SelectableDropdown>
      )}
      <FilterTextInput
        id="keyword_search"
        placeholder="Keyword Search"
        paramName="text"
        analyticsTag="text"
      />
      <SelectableDropdown title="AUM">
        <FilterChecklist filterList={aumRanges} paramName="allocator_aum" />
      </SelectableDropdown>
      <SelectableDropdown title="Firm Type">
        <FilterChecklist filterList={allocatorFirmTypes} paramName="allocator_type" />
      </SelectableDropdown>
      <SelectableDropdown title="Location" overflowVisible>
        <FilterSelect text="City" paramName="city" options={cities} />
        <FilterSelect text="Country" paramName="allocator_country" options={countries} />
      </SelectableDropdown>
      <Modal
        isOpen={isInvestmentPreferencesModalOpen}
        onClose={() => setIsInvestmentPreferencesModalOpen(false)}
        renderOpenModalButton={renderOpenInvestmentPreferencesModalButton}
        size="lg"
        title="Investment Preferences"
      >
        <div className={styles.filterGroupGrid}>
          <FilterChecklist paramName="sectors[]" filterList={sectors} title="Sectors" />
          <FilterChecklist paramName="aum_ranges[]" filterList={aumRanges} title="Aum Ranges" />
          <FilterChecklist paramName="geographies[]" filterList={geographies} title="Geographies" />
          <FilterChecklist
            paramName="asset_classes[]"
            filterList={assetClasses}
            title="Asset Classes"
          />
          <FilterChecklist paramName="fund_types[]" filterList={fundTypes} title="Fund Types" />
          <FilterChecklist
            paramName="fund_strategies[]"
            filterList={fundStrategies}
            title="Fund Strategies"
          />
        </div>
      </Modal>
    </>
  );
};

export default AllocatorProfileFilters;
